import React, { useCallback, useState } from "react";
import { ScrollView, StyleSheet, View } from "react-native";
import {
  ActivityIndicator,
  DataTable,
  IconButton,
  Surface,
  Text,
} from "react-native-paper";
import { getCompanyDocuments } from "../../../services/firestoreServices";
import { NativeStackScreenProps } from "@react-navigation/native-stack";
import { RootStackParamList } from "../../../types/navigationParams";
import { useFocusEffect } from "@react-navigation/native";
import {
  addDays,
  differenceInHours,
  endOfDay,
  format,
  startOfDay,
  subDays,
} from "date-fns";
import i18n from "../../../i18n";
import { allCategories, STATUS_OPTIONS } from "../../../config/companies/hotel";

const collectionName = "hotel-tasks";

const formatDate = (date: Date) => {
  return format(date, "dd.MM.yyyy");
};

type HotelReportsScreenType = NativeStackScreenProps<
  RootStackParamList,
  "ReportsScreen"
>;

export const HotelReportsScreen = ({ navigation }: HotelReportsScreenType) => {
  const { t } = i18n;
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [tasks, setTasks] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const goToPreviousDay = () => {
    setSelectedDate(subDays(selectedDate, 1));
  };

  const goToNextDay = () => {
    setSelectedDate(addDays(selectedDate, 1));
  };

  const goToToday = () => {
    setSelectedDate(new Date());
  };

  const isToday = (date: Date) => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  const getStatusStyle = (status: string) => {
    switch (status) {
      case "open":
        return styles.openCount;
      case "in progress":
        return styles.inProgressCount;
      case "blocked":
        return styles.blockedCount;
      case "in review":
        return styles.inReviewCount;
      case "resolved":
        return styles.resolvedCount;
      default:
        return {};
    }
  };

  useFocusEffect(
    useCallback(() => {
      setLoading(true);
      getCompanyDocuments(collectionName, {
        where: [
          {
            field: "createdAt",
            operator: ">=",
            value: startOfDay(selectedDate),
          },
          {
            field: "createdAt",
            operator: "<",
            value: endOfDay(selectedDate),
          },
        ],
      })
        .then((data) => {
          setTasks(data);
          setLoading(false);
        })
        .catch((e) => {
          setLoading(false);
          console.error(e);
        });
    }, [selectedDate]),
  );

  const renderDateNavigator = () => (
    <Surface style={styles.dateNavigator} elevation={0}>
      <IconButton icon="chevron-left" size={24} onPress={goToPreviousDay} />
      <View style={styles.dateTitleContainer}>
        <Text variant="titleMedium">{formatDate(selectedDate)}</Text>
        {isToday(selectedDate) && (
          <Text variant="labelSmall" style={styles.todayLabel}>
            {t("Today")}
          </Text>
        )}
      </View>
      <IconButton icon="chevron-right" size={24} onPress={goToNextDay} />
    </Surface>
  );

  const renderContent = () => {
    if (loading) {
      return (
        <View style={{ padding: 100 }}>
          <ActivityIndicator size="large" />
        </View>
      );
    }

    // Calculate status counts
    const totalTasks = tasks.length;

    // Get all possible statuses from hotel config
    const allStatusValues = STATUS_OPTIONS.map((option) => option.value);

    // Calculate status counts dynamically
    const statusCounts = tasks.reduce(
      (acc, task) => {
        const status = task.status || "Unknown";
        acc[status] = (acc[status] || 0) + 1;
        return acc;
      },
      {} as { [key: string]: number },
    );

    // Get all possible categories from hotel config
    const allCategoryValues = Object.values(allCategories);

    // Calculate category counts
    const categoryCounts = tasks.reduce(
      (acc, task) => {
        const category = task.category || "Unknown";
        acc[category] = (acc[category] || 0) + 1;
        return acc;
      },
      {} as { [key: string]: number },
    );

    const categoryEntries = allCategoryValues.map((category) => ({
      category,
      count: categoryCounts[category] || 0,
      percent:
        totalTasks > 0
          ? Math.round(((categoryCounts[category] || 0) / totalTasks) * 100)
          : 0,
    }));

    // Calculate executor statistics
    const executorStats = tasks.reduce(
      (acc, task) => {
        if (task.status === "resolved" && task.executor) {
          const executorId = task.executor.id || task.executor;
          const executorName = task.executor.fullName || task.executor;

          if (!acc[executorId]) {
            acc[executorId] = {
              name: executorName,
              resolvedCount: 0,
              totalTime: 0,
            };
          }

          acc[executorId].resolvedCount += 1;

          // Calculate time difference if both dates exist
          if (task.createdAt && task.statusChangedAt) {
            const createdAt = task.createdAt.toDate
              ? task.createdAt.toDate()
              : new Date(task.createdAt);
            const statusChangedAt = task.statusChangedAt.toDate
              ? task.statusChangedAt.toDate()
              : new Date(task.statusChangedAt);
            const timeDiff = differenceInHours(statusChangedAt, createdAt);
            acc[executorId].totalTime += timeDiff;
          }
        }
        return acc;
      },
      {} as {
        [key: string]: {
          name: string;
          resolvedCount: number;
          totalTime: number;
        };
      },
    );

    const executorEntries = Object.keys(executorStats).map((id) => {
      const executor = executorStats[id];
      return {
        name: executor.name,
        resolvedCount: executor.resolvedCount,
        avgTime:
          executor.resolvedCount > 0
            ? Math.round((executor.totalTime / executor.resolvedCount) * 10) /
              10
            : 0,
      };
    });

    return (
      <View style={styles.contentContainer}>
        <Text variant="headlineMedium" style={styles.totalText}>
          {t("Tasks")}: {totalTasks}
        </Text>
        <Text variant="bodyMedium" style={styles.subText}>
          {t("Tasks")} {t("dateCreated")} {formatDate(selectedDate)}
        </Text>

        <Surface style={styles.tableContainer} elevation={1}>
          <DataTable>
            <DataTable.Header>
              <DataTable.Title style={styles.statusColumn}>
                <Text variant="labelMedium">{t("Status")}</Text>
              </DataTable.Title>
              <DataTable.Title numeric style={styles.countColumn}>
                <Text variant="labelMedium">{t("Count")}</Text>
              </DataTable.Title>
            </DataTable.Header>

            <DataTable.Row>
              <DataTable.Cell style={styles.statusColumn}>
                <Text variant="bodyMedium">{t("All")}</Text>
              </DataTable.Cell>
              <DataTable.Cell numeric style={styles.countColumn}>
                <Text variant="bodyMedium" style={styles.totalCount}>
                  {totalTasks}
                </Text>
              </DataTable.Cell>
            </DataTable.Row>

            {allStatusValues.map((status) => (
              <DataTable.Row key={status}>
                <DataTable.Cell style={styles.statusColumn}>
                  <Text variant="bodyMedium">{t(status)}</Text>
                </DataTable.Cell>
                <DataTable.Cell numeric style={styles.countColumn}>
                  <Text variant="bodyMedium" style={getStatusStyle(status)}>
                    {statusCounts[status] || 0}
                  </Text>
                </DataTable.Cell>
              </DataTable.Row>
            ))}
          </DataTable>
        </Surface>

        <Surface style={styles.tableContainer} elevation={1}>
          <DataTable>
            <DataTable.Header>
              <DataTable.Title style={styles.categoryColumn}>
                <Text variant="labelMedium">{t("Type")}</Text>
              </DataTable.Title>
              <DataTable.Title numeric style={styles.countColumn}>
                <Text variant="labelMedium">{t("Count")}</Text>
              </DataTable.Title>
              <DataTable.Title numeric style={styles.percentColumn}>
                <Text variant="labelMedium">%</Text>
              </DataTable.Title>
            </DataTable.Header>

            {categoryEntries.map((entry) => (
              <DataTable.Row key={entry.category}>
                <DataTable.Cell style={styles.categoryColumn}>
                  <Text variant="bodyMedium">{entry.category}</Text>
                </DataTable.Cell>
                <DataTable.Cell numeric style={styles.countColumn}>
                  <Text variant="bodyMedium">{entry.count}</Text>
                </DataTable.Cell>
                <DataTable.Cell numeric style={styles.percentColumn}>
                  <Text variant="bodyMedium">{entry.percent}%</Text>
                </DataTable.Cell>
              </DataTable.Row>
            ))}
          </DataTable>
        </Surface>

        <Surface style={styles.tableContainer} elevation={1}>
          <DataTable>
            <DataTable.Header>
              <DataTable.Title style={styles.executorColumn}>
                <Text variant="labelMedium">{t("Executor")}</Text>
              </DataTable.Title>
              <DataTable.Title numeric style={styles.countColumn}>
                <Text variant="labelMedium">{t("resolved")}</Text>
              </DataTable.Title>
              <DataTable.Title
                numeric
                numberOfLines={2}
                style={styles.timeColumn}
              >
                <Text variant="labelMedium">{t("Avg Time (hours)")}</Text>
              </DataTable.Title>
            </DataTable.Header>

            {executorEntries.map((entry, index) => (
              <DataTable.Row key={index}>
                <DataTable.Cell style={styles.executorColumn}>
                  <Text variant="bodyMedium">{entry.name}</Text>
                </DataTable.Cell>
                <DataTable.Cell numeric style={styles.countColumn}>
                  <Text variant="bodyMedium">{entry.resolvedCount}</Text>
                </DataTable.Cell>
                <DataTable.Cell numeric style={styles.timeColumn}>
                  <Text variant="bodyMedium">{entry.avgTime}</Text>
                </DataTable.Cell>
              </DataTable.Row>
            ))}
          </DataTable>
        </Surface>
      </View>
    );
  };

  return (
    <ScrollView contentContainerStyle={{ padding: 16 }}>
      <Surface style={styles.container} elevation={1}>
        {renderDateNavigator()}
        {renderContent()}
      </Surface>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    borderRadius: 8,
  },
  dateNavigator: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    padding: 8,
    borderBottomWidth: 1,
    borderBottomColor: "rgba(0, 0, 0, 0.12)",
  },
  dateTitleContainer: {
    alignItems: "center",
  },
  todayLabel: {
    marginTop: 4,
    opacity: 0.7,
  },
  contentContainer: {
    flex: 1,
    padding: 16,
  },
  totalText: {
    marginBottom: 8,
    fontWeight: "bold",
  },
  subText: {
    color: "#666",
    marginBottom: 16,
  },
  tableContainer: {
    marginTop: 16,
    borderRadius: 8,
    flex: 1,
  },
  statusColumn: {
    flex: 2,
  },
  countColumn: {
    flex: 1,
    alignSelf: "center",
    justifyContent: "center",
  },
  categoryColumn: {
    flex: 2,
  },
  percentColumn: {
    flex: 1,
  },
  executorColumn: {
    flex: 2,
    alignSelf: "center",
  },
  timeColumn: {
    flex: 1,
    alignSelf: "center",
    justifyContent: "center",
  },
  totalCount: {
    fontWeight: "bold",
  },
  openCount: {
    color: "#3498db",
  },
  inProgressCount: {
    color: "#f39c12",
  },
  blockedCount: {
    color: "#e74c3c",
  },
  inReviewCount: {
    color: "#9b59b6",
  },
  resolvedCount: {
    color: "#2ecc71",
  },
});
